<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

import { type QingflowFile } from '@/api/exhibition';

interface Props {
  fileList: (Omit<QingflowFile, 'name'> & { type: 'image' | 'video' | 'unknown' })[];
}

const props = defineProps<Props>();

const swiperParams = {
  slidesPerView: 1,
  autoHeight: true,
  spaceBetween: 0,
  centeredSlides: true,
  grabCursor: true,
  pagination: true,
  loop: false,
  autoplay: false,
  keyboard: {
    enabled: true
  },
};

let lightbox: PhotoSwipeLightbox;

onMounted(() => {
  lightbox = new PhotoSwipeLightbox({
    gallery: '#my-gallery',
    pswpModule: () => import('photoswipe'),
    showHideOpacity: true,
    children: 'a',
    loop: false,
    showHideAnimationType: 'zoom', /* options: fade, zoom, none */

    /* Click on image moves to the next slide */
    imageClickAction: 'next',
    tapAction: 'next',

    /* ## Hiding a specific UI element ## */
    zoom: false,
    close: true,
    counter: true,
    arrowKeys: true,
    /* ## Options ## */
    bgOpacity: 0.8,/* deafult: 0.8 */
    wheelToZoom: true /* deafult: undefined */
  });

  lightbox.init();
});

onUnmounted(() => {
  lightbox?.destroy();
});

const imageDimensionsMap = ref<Record<number, { width: number, height: number }>>({});
const setImageDimensions = (url: string, index: number) => {
  const img = new Image();
  img.src = url;
  img.onload = () => {
    imageDimensionsMap.value[index] = {
      width: img.width,
      height: img.height
    };
  };
};
</script>

<template>
  <swiper-container
    id="my-gallery"
    v-bind="swiperParams"
  >
    <swiper-slide
      v-for="(file,index) of props.fileList"
      :key="file.url"
      class="slide"
    >
      <a
        v-if="file.type==='image'" :href="file.url"
        :data-pswp-width="imageDimensionsMap[index]?.width"
        :data-pswp-height="imageDimensionsMap[index]?.height"
        target="_blank"
      >
        <img :src="file.url" itemprop="thumbnail" alt="" @load="setImageDimensions(file.url,index)" />
      </a>
      <video
        v-else-if="file.type==='video'"
        preload="auto"
        controls
        :autoplay="false"
      >
        <source :src="file.url" type="video/mp4" />
      </video>
    </swiper-slide>
  </swiper-container>
</template>

<style scoped>
.slide {
  max-width: 100%;
  height: auto;
}

.slide img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.slide video {
  width: 100%;
  height: auto;
}
</style>
